import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { usePageSEO } from '../hooks/usePageSEO';
import ResumePreview from '../components/ResumePreview';
import { TEMPLATES } from '../data/samples';
import { MarketingHeader, MarketingFooter, LandingStyles, buildShowcaseResume } from './OtherPages';
import {
  ArrowRight, Play, Sparkles, Target, Wand2, FileText, Mic, LayoutGrid, Rocket,
  Check, CheckCircle2, X, Star, Users, Award, ChevronLeft, ChevronRight,
  TrendingUp, Zap, BarChart3, ThumbsUp, Quote, Layers, Lightbulb, Building2, Calendar,
  Volume2, Gauge, Crosshair, ShieldCheck, Infinity as InfinityIcon,
} from 'lucide-react';

// ── Data ───────────────────────────────────────────────────────────────────────
const WORKFLOW_WORDS = ['Create', 'Optimize', 'Tailor', 'Practice', 'Apply'];

const JOURNEY_STEPS = [
  { Icon: Sparkles, title: 'Create', desc: 'Build a complete resume from your experience in minutes with AI.', preview: 'draft' },
  { Icon: Wand2, title: 'Optimize', desc: 'AI rewrites weak bullet points into strong, results-driven statements.', preview: 'rewrite' },
  { Icon: Target, title: 'Score', desc: 'Get an instant ATS score and see exactly what to fix.', preview: 'score' },
  { Icon: Crosshair, title: 'Tailor', desc: 'Match your resume to any job description with one click.', preview: 'keywords' },
  { Icon: Mic, title: 'Practice', desc: 'Rehearse real interview questions with AI feedback.', preview: 'waveform' },
  { Icon: LayoutGrid, title: 'Track', desc: 'Track every application from applied to offer.', preview: 'kanban' },
  { Icon: Rocket, title: 'Apply', desc: 'Apply with confidence and land the interview.', preview: 'rocket' },
];

const AI_FEATURES = [
  {
    Icon: Sparkles, title: 'AI Resume Writer', desc: 'Generate polished, professional content from your raw experience.',
    details: ['Turns notes into achievement bullets', 'Writes a tailored summary', 'Matches your role and seniority'],
  },
  {
    Icon: Target, title: 'ATS Optimization', desc: 'See exactly how applicant tracking systems read your resume.',
    details: ['Keyword and formatting checks', 'Section structure review', 'Instant, specific fixes'],
  },
  {
    Icon: Wand2, title: 'Job Tailoring', desc: 'Match your resume to any job description instantly.',
    details: ['One-click keyword alignment', 'Highlights content gaps', 'Rewrites for the target role'],
  },
  {
    Icon: Mic, title: 'Interview Coach', desc: 'Practice real questions and get instant AI feedback.',
    details: ['Realistic mock questions', 'Confidence scoring', 'Answer improvement tips'],
  },
];

const RESUME_SUGGESTIONS = [
  { id: 1, weak: 'Responsible for managing a team of employees.', strong: 'Led a cross-functional team of 8, improving on-time delivery by 30%.' },
  { id: 2, weak: 'Worked on improving company sales performance.', strong: 'Drove a 22% increase in quarterly sales through targeted outreach.' },
];

const KEYWORD_BARS = [
  { label: 'Leadership', pct: 92 },
  { label: 'Data Analysis', pct: 78 },
  { label: 'Project Management', pct: 85 },
  { label: 'Communication', pct: 88 },
];

const ATS_FORMAT_CHECKS = ['Clear section headings', 'Readable formatting', 'No avoidable layout risks', 'Contact details present'];
const RECRUITER_TIPS = [
  'Lead each bullet with a strong action verb.',
  'Quantify results wherever possible — numbers stand out.',
  'Keep your summary to 2-3 focused sentences.',
];

const JOB_KEYWORDS = ['React', 'TypeScript', 'Leadership', 'Agile'];
const JOB_DESCRIPTION_PARTS = [
  'We’re looking for a Senior Engineer with strong ', { kw: 'React' }, ' and ', { kw: 'TypeScript' },
  ' experience. You’ll bring ', { kw: 'Leadership' }, ' to a growing team and thrive in an ', { kw: 'Agile' }, ' environment.',
];
const RESUME_SNIPPET_PARTS = [
  'Senior Software Engineer with 7+ years building production ', { kw: 'React' }, ' and ', { kw: 'TypeScript' },
  ' applications. Provided technical ', { kw: 'Leadership' }, ' for a 12-engineer team using ', { kw: 'Agile' }, ' methodology.',
];

const KANBAN_COLUMNS = [
  { id: 'applied', label: 'Applied', color: '#64748B', bg: '#F1F5F9', cards: [
    { company: 'Northwind Labs', role: 'Product Designer', date: 'Jul 12' },
    { company: 'Solace Health', role: 'UX Researcher', date: 'Jul 15' },
    { company: 'Brightline', role: 'Product Manager', date: 'Jul 18' },
  ] },
  { id: 'interview', label: 'Interview', color: '#1D4ED8', bg: '#EFF6FF', cards: [
    { company: 'Vector Robotics', role: 'Senior Engineer', date: 'Jul 20' },
    { company: 'Fieldstone', role: 'Data Analyst', date: 'Jul 22' },
  ] },
  { id: 'offer', label: 'Offer', color: '#059669', bg: '#ECFDF5', cards: [
    { company: 'Cobalt Systems', role: 'Frontend Engineer', date: 'Jul 24' },
  ] },
  { id: 'rejected', label: 'Rejected', color: '#94A3B8', bg: '#F8FAFC', cards: [
    { company: 'Harbor & Co.', role: 'Marketing Lead', date: 'Jul 10' },
  ] },
];

const BEFORE_AFTER_PAIRS = [
  { before: 'Responsible for managing a team of employees.', after: 'Led a cross-functional team of 8, improving on-time delivery by 30%.', tag: 'Quantified result' },
  { before: 'Helped with customer support tasks.', after: 'Resolved 40+ customer tickets weekly with a 98% satisfaction rating.', tag: 'Action verb added' },
  { before: 'In charge of social media for the company.', after: 'Grew social engagement 3x by launching a weekly content strategy.', tag: 'Specific & measurable' },
];

const WHY_CARDS = [
  { Icon: Layers, title: 'All-in-one platform', desc: 'Resume, ATS score, tailoring, interview prep, and tracking — no need for five different tools.' },
  { Icon: Target, title: 'Real ATS testing', desc: 'Every score is based on how applicant tracking systems actually parse your resume, not guesswork.' },
  { Icon: Sparkles, title: 'Context-aware AI', desc: 'Suggestions are grounded in your real experience, not generic filler text.' },
  { Icon: TrendingUp, title: 'Built for outcomes', desc: 'Every feature exists to move you one step closer to an interview, not to look impressive.' },
];

const SUCCESS_STATS = [
  { Icon: Users, value: 500, suffix: '+', label: 'Resumes built' },
  { Icon: Star, value: 4.9, decimals: 1, suffix: '★', label: 'Average rating' },
  { Icon: Layers, value: 8, suffix: '', label: 'AI tools in one platform' },
  { Icon: Award, value: 100, suffix: '+', label: 'Professionals trust us' },
];

const FAQS = [
  ['Do I need to write everything from scratch?', 'No. Start from a blank builder, rough notes, or an uploaded PDF. The AI fills in polished bullet points, a professional summary, and a skills section based on what you enter — you review and refine rather than write from zero.'],
  ['How does the AI actually help my resume?', 'You provide your job titles, responsibilities, and skills. The AI rewrites them into achievement-focused bullet points using the language recruiters and ATS systems look for. It can also generate your professional summary and tailor your entire resume to a specific job description.'],
  ['What is the ATS check and why does it matter?', 'Most large companies run every application through applicant tracking software before a human sees it. The ATS check scores your resume on keyword relevance, formatting compatibility, and readability — fixing issues before you apply meaningfully increases your callback rate.'],
  ['Can I build different versions for different jobs?', 'Yes. Build one base resume with all your experience, then create tailored copies for each role using the Job Tailoring tool. Each version is saved separately in your dashboard.'],
  ['Does MarsResume support CVs and cover letters too?', 'Yes. The builder supports Resumes, Academic CVs, and Cover Letters, all exporting to polished PDF templates.'],
  ['Is there a limit on how many documents I can create?', 'Free plan users can create a limited number of documents. Pro plan users get unlimited documents to maintain a full library of tailored resumes, CVs, and cover letters.'],
];

// ── Small shared pieces ───────────────────────────────────────────────────────
const SectionHeading = ({ title, subtitle, dark }) => (
  <div className={`hiw-sec-heading${dark ? ' hiw-sec-heading-dark' : ''}`}>
    <h2>{title}</h2>
    {subtitle && <p>{subtitle}</p>}
  </div>
);

const AnimatedCounter = ({ value, decimals = 0, duration = 1600, suffix = '', prefix = '' }) => {
  const ref = useRef(null);
  const startedRef = useRef(false);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !startedRef.current) {
        startedRef.current = true;
        const start = performance.now();
        const step = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setDisplay(value * eased);
          if (progress < 1) requestAnimationFrame(step);
          else setDisplay(value);
        };
        requestAnimationFrame(step);
        observer.disconnect();
      }
    }, { threshold: 0.4 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [value, duration]);

  return <span ref={ref}>{prefix}{display.toFixed(decimals)}{suffix}</span>;
};

const ScoreRing = ({ score, size = 140, stroke = 12, color = '#059669', trackColor = '#D1FAE5', duration = 1600 }) => {
  const ref = useRef(null);
  const startedRef = useRef(false);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !startedRef.current) {
        startedRef.current = true;
        const start = performance.now();
        const step = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setDisplay(score * eased);
          if (progress < 1) requestAnimationFrame(step);
          else setDisplay(score);
        };
        requestAnimationFrame(step);
        observer.disconnect();
      }
    }, { threshold: 0.4 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [score, duration]);

  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference * (1 - display / 100);

  return (
    <div ref={ref} style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={trackColor} strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset} transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 0.1s linear' }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: size * 0.24, fontWeight: 900, color: '#0F172A', letterSpacing: -1, lineHeight: 1 }}>{Math.round(display)}%</div>
      </div>
    </div>
  );
};

// ── Hero ───────────────────────────────────────────────────────────────────────
const DASH_ROWS = [
  { Icon: FileText, label: 'Resume Builder', sub: '3 drafts in progress', value: 'Live', color: '#EF1D28', bg: '#FFF1F2' },
  { Icon: Target, label: 'ATS Score', sub: 'Passes major scanners', value: '91%', color: '#059669', bg: '#ECFDF5' },
  { Icon: Mic, label: 'AI Interview', sub: 'Next session ready', value: 'Ready', color: '#7C3AED', bg: '#F5F3FF' },
  { Icon: LayoutGrid, label: 'Job Tracker', sub: 'This month', value: '12 apps', color: '#1D4ED8', bg: '#EFF6FF' },
];
const PARTICLES = Array.from({ length: 10 }).map((_, i) => ({
  left: `${8 + (i * 9) % 90}%`, top: `${10 + (i * 23) % 80}%`, delay: (i % 5) * 1.2, duration: 6 + (i % 4) * 1.5,
}));

const HeroSection = ({ user, navigate, onWatchDemo }) => {
  const [atsFill, setAtsFill] = useState(0);
  useEffect(() => { const t = setTimeout(() => setAtsFill(91), 400); return () => clearTimeout(t); }, []);

  return (
    <section className="hiw-hero">
      <div className="hiw-hero-grid-bg" />
      <div className="hiw-hero-glow" />
      {PARTICLES.map((p, i) => (
        <span key={i} className="hiw-particle" style={{ left: p.left, top: p.top, animationDelay: `${p.delay}s`, animationDuration: `${p.duration}s` }} />
      ))}
      <div className="hiw-hero-inner">
        <div className="hiw-hero-left">
          <h1>From blank page to<br />job offer.</h1>
          <p>Watch how MarsResume's AI guides you through every step of the job search — from writing your first resume to walking into your next interview.</p>
          <div className="hiw-workflow-strip">
            {WORKFLOW_WORDS.map((w, i) => (
              <React.Fragment key={w}>
                <span className="hiw-workflow-word">{w}</span>
                {i < WORKFLOW_WORDS.length - 1 && <ArrowRight size={13} className="hiw-workflow-sep" />}
              </React.Fragment>
            ))}
          </div>
          <div className="hiw-hero-ctas">
            {user ? (
              <button type="button" className="hiw-btn-primary" onClick={() => navigate('/dashboard')}>Go to Dashboard <ArrowRight size={16} /></button>
            ) : (
              <button type="button" className="hiw-btn-primary" onClick={() => navigate('/build/resume')}><Sparkles size={16} /> Start Free</button>
            )}
            <button type="button" className="hiw-btn-ghost-dark" onClick={onWatchDemo}><Play size={15} /> Watch Demo</button>
          </div>
        </div>
        <div className="hiw-hero-right">
          <motion.div className="hiw-dash-card" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="hiw-dash-chrome">
              <span /><span /><span />
              <div className="hiw-dash-url"><span>🔒</span> marsresume.com/dashboard</div>
            </div>
            <div className="hiw-dash-body">
              {DASH_ROWS.map(row => (
                <div key={row.label} className="hiw-dash-row">
                  <div className="hiw-dash-row-icon" style={{ background: row.bg, color: row.color }}><row.Icon size={16} /></div>
                  <div className="hiw-dash-row-text">
                    <div className="hiw-dash-row-label">{row.label}</div>
                    <div className="hiw-dash-row-sub">{row.sub}</div>
                    {row.label === 'ATS Score' && (
                      <div className="hiw-dash-row-bar"><motion.div className="hiw-dash-row-bar-fill" animate={{ width: `${atsFill}%` }} transition={{ duration: 1.2, ease: 'easeOut' }} /></div>
                    )}
                  </div>
                  <div className="hiw-dash-row-value" style={{ color: row.color, background: row.bg }}>{row.value}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ── Career journey ────────────────────────────────────────────────────────────
const JourneyPreview = ({ type }) => {
  if (type === 'draft') return <div className="hiw-jp hiw-jp-draft"><span /><span /><span className="short" /></div>;
  if (type === 'rewrite') return (
    <div className="hiw-jp hiw-jp-rewrite">
      <div className="weak">Worked on team tasks</div>
      <ArrowRight size={11} />
      <div className="strong">Led team of 8</div>
    </div>
  );
  if (type === 'score') return (
    <div className="hiw-jp hiw-jp-score">
      <svg width="46" height="46" viewBox="0 0 46 46">
        <circle cx="23" cy="23" r="19" fill="none" stroke="#D1FAE5" strokeWidth="6" />
        <circle cx="23" cy="23" r="19" fill="none" stroke="#059669" strokeWidth="6" strokeLinecap="round" strokeDasharray="119" strokeDashoffset="14" transform="rotate(-90 23 23)" />
      </svg>
      <span>91%</span>
    </div>
  );
  if (type === 'keywords') return (
    <div className="hiw-jp hiw-jp-keywords">
      {['React', 'Agile', 'Lead'].map(k => <span key={k}>{k}</span>)}
    </div>
  );
  if (type === 'waveform') return (
    <div className="hiw-jp hiw-jp-waveform">
      {[6, 14, 9, 18, 7, 12, 16].map((h, i) => <span key={i} style={{ height: h }} />)}
    </div>
  );
  if (type === 'kanban') return (
    <div className="hiw-jp hiw-jp-kanban">
      {['#EF1D28', '#1D4ED8', '#059669'].map(c => <span key={c} style={{ background: c }} />)}
    </div>
  );
  return <div className="hiw-jp hiw-jp-rocket"><Rocket size={20} color="#EF1D28" /></div>;
};

const JourneySection = () => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 900);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const track = (
    <motion.div className="hiw-journey-track" initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.1 }}
      variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1 } } }}>
      {JOURNEY_STEPS.map((step, i) => (
        <React.Fragment key={step.title}>
          <motion.div className="hiw-journey-card" variants={{ hidden: { opacity: 0, y: 30 }, show: { opacity: 1, y: 0 } }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} whileHover={{ y: -8 }}>
            <div className="hiw-journey-num">{String(i + 1).padStart(2, '0')}</div>
            <div className="hiw-journey-icon"><step.Icon size={20} /></div>
            <div className="hiw-journey-title">{step.title}</div>
            <p className="hiw-journey-desc">{step.desc}</p>
            <JourneyPreview type={step.preview} />
          </motion.div>
          {i < JOURNEY_STEPS.length - 1 && (
            <div className="hiw-journey-arrow">
              <motion.div className="hiw-journey-arrow-line" style={{ transformOrigin: isMobile ? 'top' : 'left' }}
                initial={isMobile ? { scaleY: 0 } : { scaleX: 0 }} whileInView={isMobile ? { scaleY: 1 } : { scaleX: 1 }}
                viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.15 + i * 0.06 }} />
              <ArrowRight size={14} className="hiw-journey-arrow-icon" />
            </div>
          )}
        </React.Fragment>
      ))}
    </motion.div>
  );

  return (
    <section className="hiw-section">
      <SectionHeading title="Your complete career journey" subtitle="Seven connected steps. One platform. From your first draft to your next offer." />
      {isMobile ? track : <div className="hiw-journey-scroll">{track}</div>}
    </section>
  );
};

// ── Resume Builder live demo ─────────────────────────────────────────────────
const ResumeBuilderDemo = () => {
  const [bullets, setBullets] = useState(RESUME_SUGGESTIONS.map(s => s.weak));
  const [status, setStatus] = useState({});

  const accept = (idx) => {
    setStatus(prev => ({ ...prev, [idx]: 'accepted' }));
    setBullets(prev => prev.map((b, i) => i === idx ? RESUME_SUGGESTIONS[idx].strong : b));
  };
  const dismiss = (idx) => setStatus(prev => ({ ...prev, [idx]: 'dismissed' }));

  return (
    <section className="hiw-section" id="hiw-demo">
      <SectionHeading title="Try the AI writing assistant" subtitle="Weak bullet points go in. Recruiter-ready achievements come out. Try it below." />
      <div className="hiw-demo-grid">
        <div className="hiw-demo-editor">
          <div className="hiw-demo-editor-head"><FileText size={14} /> Work Experience</div>
          {bullets.map((b, i) => (
            <motion.div key={i} className={`hiw-demo-bullet${status[i] === 'accepted' ? ' improved' : ''}`} layout transition={{ duration: 0.4 }}>
              {status[i] === 'accepted' && <span className="hiw-demo-bullet-tag"><CheckCircle2 size={11} /> Improved</span>}
              <p>{b}</p>
            </motion.div>
          ))}
        </div>
        <div className="hiw-demo-suggestions">
          <div className="hiw-demo-editor-head"><Sparkles size={14} color="#7C3AED" /> AI Suggestions</div>
          <AnimatePresence>
            {RESUME_SUGGESTIONS.map((s, i) => status[i] !== undefined ? (
              <motion.div key={s.id} className="hiw-demo-suggestion-done" initial={{ opacity: 1 }} animate={{ opacity: 0.6 }}>
                {status[i] === 'accepted' ? <><CheckCircle2 size={14} color="#059669" /> Applied</> : <><X size={14} color="#94A3B8" /> Dismissed</>}
              </motion.div>
            ) : (
              <motion.div key={s.id} className="hiw-demo-suggestion-card" initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.15 }}>
                <div className="hiw-demo-suggestion-weak"><X size={12} /> {s.weak}</div>
                <div className="hiw-demo-suggestion-strong"><Sparkles size={12} /> {s.strong}</div>
                <div className="hiw-demo-suggestion-actions">
                  <button type="button" className="hiw-demo-btn-accept" onClick={() => accept(i)}>Accept</button>
                  <button type="button" className="hiw-demo-btn-dismiss" onClick={() => dismiss(i)}>Dismiss</button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

// ── AI feature section ───────────────────────────────────────────────────────
const AIFeatureSection = () => (
  <section className="hiw-section hiw-section-tint">
    <SectionHeading title="Four tools. One outcome." subtitle="Everything works together to get your resume in front of the right people." />
    <div className="hiw-ai-grid">
      {AI_FEATURES.map(f => (
        <motion.div key={f.title} className="hiw-ai-card" whileHover={{ y: -8 }} transition={{ type: 'spring', stiffness: 300, damping: 22 }}>
          <div className="hiw-ai-icon"><f.Icon size={22} /></div>
          <div className="hiw-ai-title">{f.title}</div>
          <p className="hiw-ai-desc">{f.desc}</p>
          <div className="hiw-ai-details">
            {f.details.map(d => <div key={d} className="hiw-ai-detail"><Check size={12} /> {d}</div>)}
          </div>
        </motion.div>
      ))}
    </div>
  </section>
);

// ── ATS visual report ────────────────────────────────────────────────────────
const ATS_TEMPLATE = TEMPLATES.find(t => t.id === 'harvard_standard') || TEMPLATES[0];
const ATSReportSection = () => {
  const preview = buildShowcaseResume(ATS_TEMPLATE);
  return (
    <section className="hiw-section">
      <SectionHeading title="A real ATS report, not a guess" subtitle="See exactly what applicant tracking systems see when they scan your resume." />
      <div className="hiw-ats-card">
        <div className="hiw-ats-left">
          <div className="hiw-ats-score-row">
            <ScoreRing score={91} />
            <div>
              <div className="hiw-ats-score-title">Strong ATS Score</div>
              <p>Your resume is well-structured and keyword-optimized for this role.</p>
            </div>
          </div>
          <div className="hiw-ats-bars">
            {KEYWORD_BARS.map(k => (
              <div key={k.label} className="hiw-ats-bar-row">
                <div className="hiw-ats-bar-label"><span>{k.label}</span><span>{k.pct}%</span></div>
                <div className="hiw-ats-bar-track"><motion.div className="hiw-ats-bar-fill" initial={{ width: 0 }} whileInView={{ width: `${k.pct}%` }} viewport={{ once: true }} transition={{ duration: 1, ease: 'easeOut' }} /></div>
              </div>
            ))}
          </div>
          <div className="hiw-ats-checks">
            {ATS_FORMAT_CHECKS.map(c => <div key={c} className="hiw-ats-check"><Check size={13} /> {c}</div>)}
          </div>
          <div className="hiw-ats-tips">
            <div className="hiw-ats-tips-title"><Lightbulb size={14} /> Recruiter tips</div>
            {RECRUITER_TIPS.map(t => <p key={t}>{t}</p>)}
          </div>
        </div>
        <div className="hiw-ats-right">
          <div className="hiw-ats-preview-frame">
            <ResumePreview data={preview} id="hiw-ats-preview" singlePage />
          </div>
        </div>
      </div>
    </section>
  );
};

// ── Job Tailoring demo ───────────────────────────────────────────────────────
const KeywordText = ({ parts, active }) => (
  <p>
    {parts.map((p, i) => typeof p === 'string' ? <span key={i}>{p}</span> : (
      <span key={i} className={`hiw-kw${active ? ' active' : ''}`}>{p.kw}</span>
    ))}
  </p>
);

const JobTailoringSection = () => {
  const [tailored, setTailored] = useState(false);
  return (
    <section className="hiw-section hiw-section-tint">
      <SectionHeading title="Tailor to any job in seconds" subtitle="Paste a job description. Watch your resume align, keyword by keyword." />
      <div className="hiw-tailor-card">
        <div className="hiw-tailor-grid">
          <div className="hiw-tailor-col">
            <div className="hiw-tailor-col-head"><Building2 size={14} /> Job Description</div>
            <div className="hiw-tailor-text"><KeywordText parts={JOB_DESCRIPTION_PARTS} active={tailored} /></div>
          </div>
          <div className="hiw-tailor-col">
            <div className="hiw-tailor-col-head"><FileText size={14} /> Your Resume</div>
            <div className="hiw-tailor-text"><KeywordText parts={RESUME_SNIPPET_PARTS} active={tailored} /></div>
          </div>
        </div>
        <div className="hiw-tailor-footer">
          <div className="hiw-tailor-score">
            <span>ATS Match Score</span>
            <div className="hiw-tailor-score-row">
              <AnimatePresence mode="wait">
                <motion.span key={tailored ? 'after' : 'before'} className="hiw-tailor-score-value" style={{ color: tailored ? '#059669' : '#D97706' }}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }}>
                  {tailored ? '94%' : '72%'}
                </motion.span>
              </AnimatePresence>
              {tailored && <span className="hiw-tailor-score-delta"><TrendingUp size={13} /> +22%</span>}
            </div>
          </div>
          <button type="button" className="hiw-btn-primary" onClick={() => setTailored(v => !v)}>
            {tailored ? 'Reset' : 'Tailor Resume'} <Wand2 size={16} />
          </button>
        </div>
      </div>
    </section>
  );
};

// ── AI Interview Practice ─────────────────────────────────────────────────────
const WAVEFORM_BARS = Array.from({ length: 24 }).map((_, i) => 0.2 + ((i * 37) % 60) / 100);
const InterviewPracticeSection = ({ navigate }) => {
  const [isPracticing, setIsPracticing] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  const handleStart = () => {
    setShowFeedback(false);
    setIsPracticing(true);
    setTimeout(() => { setIsPracticing(false); setShowFeedback(true); }, 3200);
  };

  return (
    <section className="hiw-section">
      <SectionHeading title="Practice before it counts" subtitle="Rehearse real interview questions and get instant, specific feedback." />
      <div className="hiw-interview-card">
        <div className="hiw-interview-question">
          <div className="hiw-interview-q-label">Question</div>
          <p>"Tell me about a time you led a difficult project."</p>
        </div>
        <div className="hiw-interview-waveform">
          {WAVEFORM_BARS.map((h, i) => (
            <motion.span key={i} animate={{ scaleY: isPracticing ? [h, 1, h * 0.6, h] : 0.15 }}
              transition={{ duration: 1.1, repeat: isPracticing ? Infinity : 0, delay: i * 0.03, ease: 'easeInOut' }} />
          ))}
        </div>
        <div className="hiw-interview-actions">
          <button type="button" className="hiw-btn-primary" onClick={handleStart} disabled={isPracticing}>
            <Volume2 size={16} /> {isPracticing ? 'Listening…' : 'Start Practice Answer'}
          </button>
        </div>
        <AnimatePresence>
          {showFeedback && (
            <motion.div className="hiw-interview-feedback" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.4 }}>
              <div className="hiw-interview-feedback-row">
                <ScoreRing score={87} size={90} stroke={9} color="#7C3AED" trackColor="#EDE9FE" />
                <div>
                  <div className="hiw-interview-feedback-title">Confidence Score</div>
                  <p>Strong structure — you clearly stated the situation and outcome.</p>
                </div>
              </div>
              <div className="hiw-interview-improvements">
                <div className="hiw-interview-improvements-title"><Lightbulb size={14} /> Answer improvements</div>
                <div className="hiw-interview-improvement"><Check size={12} /> Add a specific, measurable result.</div>
                <div className="hiw-interview-improvement"><Check size={12} /> Slow down slightly on the key outcome.</div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <div className="hiw-interview-cta">
          <button type="button" className="hiw-btn-secondary" onClick={() => navigate('/ai-video-interview')}>Practice Interview <ArrowRight size={15} /></button>
        </div>
      </div>
    </section>
  );
};

// ── Job Tracker Kanban ───────────────────────────────────────────────────────
const JobTrackerSection = () => (
  <section className="hiw-section hiw-section-tint">
    <SectionHeading title="Never lose track of an application" subtitle="See every application, interview, and offer in one dashboard." />
    <div className="hiw-kanban-scroll">
      <div className="hiw-kanban-board">
        {KANBAN_COLUMNS.map(col => (
          <div key={col.id} className="hiw-kanban-col">
            <div className="hiw-kanban-col-head">
              <span className="hiw-kanban-col-dot" style={{ background: col.color }} />
              {col.label}
              <span className="hiw-kanban-col-count">{col.cards.length}</span>
            </div>
            <div className="hiw-kanban-cards">
              {col.cards.map(c => (
                <motion.div key={c.company} className="hiw-kanban-card" whileHover={{ y: -3 }} style={{ borderLeftColor: col.color }}>
                  <div className="hiw-kanban-card-role">{c.role}</div>
                  <div className="hiw-kanban-card-company"><Building2 size={12} /> {c.company}</div>
                  <div className="hiw-kanban-card-date"><Calendar size={11} /> {c.date}</div>
                  <span className="hiw-kanban-card-badge" style={{ background: col.bg, color: col.color }}>{col.label}</span>
                </motion.div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ── Before vs After ──────────────────────────────────────────────────────────
const BeforeAfterSection = () => (
  <section className="hiw-section">
    <SectionHeading title="Before vs. after" subtitle="The same experience, rewritten to actually get noticed." />
    <div className="hiw-ba-list">
      {BEFORE_AFTER_PAIRS.map((pair, i) => (
        <motion.div key={pair.before} className="hiw-ba-row" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.4, delay: i * 0.08 }}>
          <div className="hiw-ba-col hiw-ba-before">
            <div className="hiw-ba-label">Before</div>
            <p>{pair.before}</p>
          </div>
          <ArrowRight size={18} className="hiw-ba-arrow" />
          <div className="hiw-ba-col hiw-ba-after">
            <div className="hiw-ba-label">After</div>
            <p>{pair.after}</p>
            <span className="hiw-ba-tag"><CheckCircle2 size={11} /> {pair.tag}</span>
          </div>
        </motion.div>
      ))}
    </div>
  </section>
);

// ── Why MarsResume ────────────────────────────────────────────────────────────
const WhySection = () => (
  <section className="hiw-section hiw-section-tint">
    <SectionHeading title="Why MarsResume works" subtitle="Built around one goal: getting your resume past the filters and in front of a human." />
    <div className="hiw-why-grid">
      {WHY_CARDS.map(c => (
        <motion.div key={c.title} className="hiw-why-card" whileHover={{ y: -6 }} transition={{ type: 'spring', stiffness: 300, damping: 22 }}>
          <div className="hiw-why-icon"><c.Icon size={20} /></div>
          <div className="hiw-why-title">{c.title}</div>
          <p>{c.desc}</p>
        </motion.div>
      ))}
    </div>
  </section>
);

// ── Success metrics ───────────────────────────────────────────────────────────
const MetricsSection = () => (
  <section className="hiw-section">
    <motion.div className="hiw-metrics-grid" initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }}
      variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}>
      {SUCCESS_STATS.map(stat => (
        <motion.div key={stat.label} className="hiw-metric-card" variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }} transition={{ duration: 0.4 }} whileHover={{ y: -4 }}>
          <div className="hiw-metric-icon"><stat.Icon size={20} /></div>
          <div className="hiw-metric-value"><AnimatedCounter value={stat.value} decimals={stat.decimals || 0} suffix={stat.suffix} /></div>
          <div className="hiw-metric-label">{stat.label}</div>
        </motion.div>
      ))}
    </motion.div>
  </section>
);

// ── FAQ ────────────────────────────────────────────────────────────────────────
const FAQSection = () => {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <section className="hiw-section">
      <SectionHeading title="Frequently asked questions" />
      <div className="hiw-faq-list">
        {FAQS.map(([q, a], i) => {
          const isOpen = openIdx === i;
          return (
            <div key={q} className={`hiw-faq-item${isOpen ? ' open' : ''}`}>
              <button type="button" className="hiw-faq-trigger" aria-expanded={isOpen} onClick={() => setOpenIdx(isOpen ? null : i)}>
                <span>{q}</span>
                <span className="hiw-faq-icon">
                  <span className="bar h" />
                  <motion.span className="bar v" animate={{ rotate: isOpen ? 90 : 0 }} transition={{ duration: 0.25, ease: 'easeInOut' }} />
                </span>
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} style={{ overflow: 'hidden' }}>
                    <div className="hiw-faq-answer">{a}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </section>
  );
};

// ── Final CTA ──────────────────────────────────────────────────────────────────
const FinalCTASection = ({ user, navigate }) => (
  <section className="hiw-cta-outer">
    <motion.div className="hiw-cta-card" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.5 }}>
      <div className="hiw-cta-mesh" />
      <div className="hiw-cta-glow" />
      <h2>Ready to see it work for you?</h2>
      {user ? (
        <button type="button" className="hiw-btn-primary" onClick={() => navigate('/dashboard')}>Go to Dashboard <ArrowRight size={16} /></button>
      ) : (
        <button type="button" className="hiw-btn-primary" onClick={() => navigate('/build/resume')}><Sparkles size={16} /> Start Building Free</button>
      )}
      <div className="hiw-cta-sub">Free forever &middot; No credit card required</div>
    </motion.div>
  </section>
);

// ── Main page ────────────────────────────────────────────────────────────────
export const HowItWorksPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  usePageSEO({
    title: 'How It Works - MarsResume | Build a Resume in Minutes',
    description: 'See how MarsResume helps you create, improve, tailor, and export an ATS-friendly resume with AI-powered tools — from first draft to job offer.',
    canonical: '/how-it-works',
  });

  const goTo = (path) => navigate(user ? (path === '/signup' ? '/dashboard' : path) : path);
  const demoRef = useRef(null);
  const scrollToDemo = () => demoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="hiw-page">
      <LandingStyles />
      <HowItWorksStyles />
      <MarketingHeader />

      <HeroSection user={user} navigate={navigate} onWatchDemo={scrollToDemo} />
      <JourneySection />
      <div ref={demoRef}><ResumeBuilderDemo /></div>
      <AIFeatureSection />
      <ATSReportSection />
      <JobTailoringSection />
      <InterviewPracticeSection navigate={navigate} />
      <JobTrackerSection />
      <BeforeAfterSection />
      <WhySection />
      <MetricsSection />
      <FAQSection />
      <FinalCTASection user={user} navigate={navigate} />

      <MarketingFooter navigate={navigate} goTo={goTo} />
    </div>
  );
};

// ── Styles ─────────────────────────────────────────────────────────────────────
const HowItWorksStyles = () => (
  <style>{`
    .hiw-page { min-height: 100vh; background: #F9FAFB; font-family: 'Inter', sans-serif; color: #111827; overflow-x: hidden; }

    @keyframes hiwHeroFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
    @keyframes hiwParticle { 0%{transform:translate(0,0); opacity:0;} 12%{opacity:0.8;} 88%{opacity:0.6;} 100%{transform:translate(14px,-110px); opacity:0;} }

    /* Buttons */
    .hiw-btn-primary { background: #EF1D28; color: white; border: none; border-radius: 12px; padding: 14px 26px; font-weight: 700; font-size: 15px; cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 8px; transition: all 0.2s; box-shadow: 0 6px 20px rgba(239,29,40,0.3); }
    .hiw-btn-primary:hover:not(:disabled) { background: #D91620; transform: translateY(-2px); box-shadow: 0 10px 28px rgba(239,29,40,0.4); }
    .hiw-btn-primary:disabled { opacity: 0.7; cursor: default; }
    .hiw-btn-ghost-dark { background: rgba(255,255,255,0.06); border: 1.5px solid rgba(255,255,255,0.2); color: white; border-radius: 12px; padding: 14px 26px; font-weight: 700; font-size: 15px; cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 8px; transition: all 0.2s; }
    .hiw-btn-ghost-dark:hover { background: rgba(255,255,255,0.12); border-color: rgba(255,255,255,0.35); transform: translateY(-2px); }
    .hiw-btn-secondary { background: white; border: 1.5px solid #E5E7EB; color: #374151; border-radius: 12px; padding: 13px 24px; font-weight: 700; font-size: 14px; cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 8px; transition: all 0.2s; }
    .hiw-btn-secondary:hover { border-color: #CBD5E1; background: #F9FAFB; }

    /* Section heading */
    .hiw-section { padding: 76px 48px; max-width: 1180px; margin: 0 auto; }
    .hiw-section-tint { background: #FAFBFF; max-width: none; padding-left: 48px; padding-right: 48px; }
    .hiw-section-tint > * { max-width: 1180px; margin-left: auto; margin-right: auto; }
    .hiw-sec-heading { text-align: center; margin-bottom: 48px; }
    .hiw-sec-heading h2 { margin: 0; font-size: 32px; font-weight: 900; color: #0F172A; letter-spacing: -0.8px; }
    .hiw-sec-heading p { margin: 12px auto 0; color: #64748B; font-size: 15px; line-height: 1.65; max-width: 560px; }

    /* Hero */
    .hiw-hero { background: linear-gradient(160deg,#070E1A 0%,#0D1120 50%,#0A1420 100%); padding: 88px 48px 96px; position: relative; overflow: hidden; }
    .hiw-hero-grid-bg { position: absolute; inset: 0; background-image: radial-gradient(rgba(255,255,255,0.06) 1px,transparent 1px); background-size: 28px 28px; pointer-events: none; }
    .hiw-hero-glow { position: absolute; top: 10%; left: 15%; width: 700px; height: 420px; background: radial-gradient(ellipse, rgba(239,29,40,0.14) 0%, transparent 70%); pointer-events: none; }
    .hiw-particle { position: absolute; width: 4px; height: 4px; border-radius: 50%; background: rgba(239,29,40,0.7); animation-name: hiwParticle; animation-timing-function: ease-in-out; animation-iteration-count: infinite; pointer-events: none; }
    .hiw-hero-inner { max-width: 1180px; margin: 0 auto; display: flex; align-items: center; gap: 56px; position: relative; }
    .hiw-hero-left { flex: 0 0 46%; }
    .hiw-hero-left h1 { margin: 0 0 20px; font-size: 50px; line-height: 1.08; font-weight: 900; letter-spacing: -1.6px; color: white; }
    .hiw-hero-left p { margin: 0 0 24px; font-size: 17px; line-height: 1.7; color: #94A3B8; max-width: 460px; }
    .hiw-workflow-strip { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 32px; }
    .hiw-workflow-word { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.14); color: #E2E8F0; font-size: 12.5px; font-weight: 700; padding: 6px 13px; border-radius: 999px; }
    .hiw-workflow-sep { color: #EF1D28; flex-shrink: 0; }
    .hiw-hero-ctas { display: flex; gap: 12px; flex-wrap: wrap; }
    .hiw-hero-right { flex: 1; display: flex; align-items: center; justify-content: center; position: relative; min-height: 400px; }

    .hiw-dash-card { width: 420px; background: rgba(255,255,255,0.96); backdrop-filter: blur(18px); border-radius: 20px; box-shadow: 0 40px 90px rgba(0,0,0,0.45); border: 1px solid rgba(255,255,255,0.1); overflow: hidden; animation: hiwHeroFloat 6.5s ease-in-out infinite; position: relative; z-index: 2; }
    .hiw-dash-chrome { background: #F1F5F9; padding: 11px 14px; display: flex; align-items: center; gap: 7px; border-bottom: 1px solid #E2E8F0; }
    .hiw-dash-chrome span { width: 10px; height: 10px; border-radius: 50%; background: #CBD5E1; display: inline-block; }
    .hiw-dash-chrome span:nth-child(1) { background: #FC5F5A; }
    .hiw-dash-chrome span:nth-child(2) { background: #FDBA2F; }
    .hiw-dash-chrome span:nth-child(3) { background: #28C840; }
    .hiw-dash-url { flex: 1; background: white; border-radius: 6px; padding: 4px 10px; margin-left: 6px; font-size: 10px; color: #94A3B8; display: flex; align-items: center; gap: 5px; border: 1px solid #E2E8F0; }
    .hiw-dash-body { padding: 16px; display: flex; flex-direction: column; gap: 10px; }
    .hiw-dash-row { display: flex; align-items: center; gap: 12px; padding: 10px; border: 1px solid #F1F5F9; border-radius: 12px; background: #FAFBFF; }
    .hiw-dash-row-icon { width: 34px; height: 34px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .hiw-dash-row-text { flex: 1; min-width: 0; }
    .hiw-dash-row-label { font-size: 12.5px; font-weight: 800; color: #111827; }
    .hiw-dash-row-sub { font-size: 10.5px; color: #94A3B8; margin-top: 1px; }
    .hiw-dash-row-bar { margin-top: 6px; height: 4px; border-radius: 2px; background: #E2E8F0; overflow: hidden; }
    .hiw-dash-row-bar-fill { height: 100%; background: linear-gradient(90deg,#34D399,#059669); border-radius: 2px; }
    .hiw-dash-row-value { font-size: 11.5px; font-weight: 800; padding: 4px 9px; border-radius: 7px; flex-shrink: 0; }

    /* Journey */
    .hiw-journey-scroll { overflow-x: auto; scrollbar-width: none; -ms-overflow-style: none; margin: 0 -48px; padding: 4px 48px 16px; }
    .hiw-journey-scroll::-webkit-scrollbar { display: none; }
    .hiw-journey-track { display: flex; align-items: stretch; gap: 0; width: max-content; }
    .hiw-journey-card { position: relative; width: 240px; background: white; border: 1px solid #E5E7EB; border-radius: 20px; padding: 24px 20px; box-shadow: 0 4px 24px rgba(15,23,42,0.05); transition: box-shadow 0.25s ease, border-color 0.25s ease; }
    .hiw-journey-card:hover { box-shadow: 0 20px 48px rgba(15,23,42,0.1); border-color: #FBD3D5; }
    .hiw-journey-num { position: absolute; top: 20px; right: 20px; font-size: 11px; font-weight: 800; color: #E2E8F0; }
    .hiw-journey-icon { width: 42px; height: 42px; border-radius: 12px; background: linear-gradient(135deg,#EF1D28,#FF6B6B); color: white; display: flex; align-items: center; justify-content: center; margin-bottom: 14px; box-shadow: 0 6px 16px rgba(239,29,40,0.28); }
    .hiw-journey-title { font-size: 15.5px; font-weight: 800; color: #0F172A; margin-bottom: 6px; }
    .hiw-journey-desc { margin: 0 0 16px; color: #64748B; font-size: 12.5px; line-height: 1.55; min-height: 54px; }
    .hiw-journey-arrow { width: 36px; display: flex; align-items: center; justify-content: center; position: relative; flex-shrink: 0; }
    .hiw-journey-arrow-line { width: 100%; height: 2px; background: linear-gradient(90deg,#FED7D7,#EF1D28); border-radius: 2px; }
    .hiw-journey-arrow-icon { position: absolute; color: #EF1D28; background: white; border-radius: 50%; padding: 2px; }

    .hiw-jp { border-radius: 10px; background: #FAFBFF; border: 1px solid #F1F5F9; padding: 10px; display: flex; align-items: center; justify-content: center; min-height: 52px; }
    .hiw-jp-draft { flex-direction: column; align-items: flex-start; gap: 5px; }
    .hiw-jp-draft span { display: block; height: 5px; border-radius: 3px; background: #E2E8F0; width: 100%; }
    .hiw-jp-draft span.short { width: 60%; }
    .hiw-jp-rewrite { flex-direction: column; gap: 4px; font-size: 9.5px; }
    .hiw-jp-rewrite .weak { color: #94A3B8; text-decoration: line-through; }
    .hiw-jp-rewrite .strong { color: #059669; font-weight: 700; }
    .hiw-jp-rewrite svg { align-self: center; color: #CBD5E1; }
    .hiw-jp-score { gap: 8px; }
    .hiw-jp-score span { font-size: 13px; font-weight: 900; color: #059669; }
    .hiw-jp-keywords { gap: 4px; flex-wrap: wrap; }
    .hiw-jp-keywords span { background: #FFF1F2; color: #DC2626; font-size: 9.5px; font-weight: 700; padding: 3px 7px; border-radius: 999px; }
    .hiw-jp-waveform { gap: 2px; align-items: center; }
    .hiw-jp-waveform span { width: 3px; border-radius: 2px; background: #EF1D28; display: inline-block; }
    .hiw-jp-kanban { gap: 5px; }
    .hiw-jp-kanban span { width: 8px; height: 24px; border-radius: 3px; }

    /* Resume Builder demo */
    .hiw-demo-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; max-width: 1000px; margin: 0 auto; }
    .hiw-demo-editor, .hiw-demo-suggestions { background: white; border: 1px solid #E5E7EB; border-radius: 20px; padding: 24px; box-shadow: 0 4px 24px rgba(15,23,42,0.05); }
    .hiw-demo-editor-head { display: flex; align-items: center; gap: 8px; font-size: 12px; font-weight: 800; color: #94A3B8; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 16px; }
    .hiw-demo-bullet { background: #FAFBFF; border: 1px solid #F1F5F9; border-radius: 12px; padding: 14px 16px; margin-bottom: 10px; }
    .hiw-demo-bullet.improved { border-color: #BBF7D0; background: #F0FDF4; }
    .hiw-demo-bullet p { margin: 0; font-size: 13.5px; color: #374151; line-height: 1.6; }
    .hiw-demo-bullet-tag { display: inline-flex; align-items: center; gap: 5px; font-size: 10.5px; font-weight: 800; color: #059669; margin-bottom: 6px; }
    .hiw-demo-suggestion-card { background: #FAFBFF; border: 1.5px solid #E2E8F0; border-radius: 14px; padding: 16px; margin-bottom: 14px; }
    .hiw-demo-suggestion-weak { display: flex; align-items: flex-start; gap: 6px; font-size: 12.5px; color: #94A3B8; text-decoration: line-through; margin-bottom: 8px; line-height: 1.5; }
    .hiw-demo-suggestion-strong { display: flex; align-items: flex-start; gap: 6px; font-size: 13px; color: #111827; font-weight: 600; margin-bottom: 12px; line-height: 1.55; }
    .hiw-demo-suggestion-strong svg { color: #7C3AED; margin-top: 2px; flex-shrink: 0; }
    .hiw-demo-suggestion-weak svg { margin-top: 2px; flex-shrink: 0; }
    .hiw-demo-suggestion-actions { display: flex; gap: 8px; }
    .hiw-demo-btn-accept { flex: 1; border: none; background: #0F172A; color: white; border-radius: 9px; padding: 9px 0; font-size: 12.5px; font-weight: 800; cursor: pointer; font-family: inherit; }
    .hiw-demo-btn-accept:hover { background: #1E293B; }
    .hiw-demo-btn-dismiss { flex: 1; border: 1.5px solid #E5E7EB; background: white; color: #64748B; border-radius: 9px; padding: 9px 0; font-size: 12.5px; font-weight: 700; cursor: pointer; font-family: inherit; }
    .hiw-demo-btn-dismiss:hover { border-color: #CBD5E1; }
    .hiw-demo-suggestion-done { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 700; color: #64748B; padding: 14px 16px; }

    /* AI feature grid */
    .hiw-ai-grid { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 18px; }
    .hiw-ai-card { background: white; border: 1px solid #E5E7EB; border-radius: 20px; padding: 26px 22px; transition: box-shadow 0.25s ease, border-color 0.25s ease; }
    .hiw-ai-card:hover { box-shadow: 0 20px 48px rgba(15,23,42,0.1); border-color: #FBD3D5; }
    .hiw-ai-icon { width: 46px; height: 46px; border-radius: 13px; background: linear-gradient(135deg,#EF1D28,#FF6B6B); color: white; display: flex; align-items: center; justify-content: center; margin-bottom: 16px; box-shadow: 0 8px 20px rgba(239,29,40,0.25); }
    .hiw-ai-title { font-size: 15.5px; font-weight: 800; color: #0F172A; margin-bottom: 8px; }
    .hiw-ai-desc { margin: 0; color: #64748B; font-size: 13px; line-height: 1.6; }
    .hiw-ai-details { max-height: 0; overflow: hidden; transition: max-height 0.35s ease, margin-top 0.35s ease; margin-top: 0; }
    .hiw-ai-card:hover .hiw-ai-details { max-height: 140px; margin-top: 14px; }
    .hiw-ai-detail { display: flex; align-items: center; gap: 7px; font-size: 12px; color: #374151; font-weight: 600; padding: 5px 0; border-top: 1px solid #F1F5F9; }
    .hiw-ai-detail svg { color: #059669; flex-shrink: 0; }

    /* ATS report */
    .hiw-ats-card { max-width: 1080px; margin: 0 auto; background: white; border: 1px solid #E5E7EB; border-radius: 24px; box-shadow: 0 12px 40px rgba(15,23,42,0.06); display: grid; grid-template-columns: 1.3fr 1fr; overflow: hidden; }
    .hiw-ats-left { padding: 36px; }
    .hiw-ats-score-row { display: flex; align-items: center; gap: 20px; margin-bottom: 28px; }
    .hiw-ats-score-title { font-size: 17px; font-weight: 800; color: #0F172A; margin-bottom: 6px; }
    .hiw-ats-score-row p { margin: 0; color: #64748B; font-size: 13px; line-height: 1.6; max-width: 280px; }
    .hiw-ats-bars { display: flex; flex-direction: column; gap: 14px; margin-bottom: 26px; }
    .hiw-ats-bar-label { display: flex; justify-content: space-between; font-size: 12.5px; font-weight: 700; color: #374151; margin-bottom: 6px; }
    .hiw-ats-bar-track { height: 6px; border-radius: 3px; background: #F1F5F9; overflow: hidden; }
    .hiw-ats-bar-fill { height: 100%; border-radius: 3px; background: linear-gradient(90deg,#34D399,#059669); }
    .hiw-ats-checks { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 8px; margin-bottom: 24px; }
    .hiw-ats-check { display: flex; align-items: center; gap: 7px; background: #ECFDF5; border: 1px solid #BBF7D0; color: #065F46; border-radius: 9px; padding: 8px 10px; font-size: 12px; font-weight: 700; }
    .hiw-ats-tips { background: #FFFBEB; border: 1px solid #FDE68A; border-radius: 14px; padding: 16px 18px; }
    .hiw-ats-tips-title { display: flex; align-items: center; gap: 7px; font-size: 12.5px; font-weight: 800; color: #92400E; margin-bottom: 8px; }
    .hiw-ats-tips p { margin: 0 0 6px; font-size: 12.5px; color: #78350F; line-height: 1.6; }
    .hiw-ats-tips p:last-child { margin-bottom: 0; }
    .hiw-ats-right { background: linear-gradient(180deg,#F1F5F9 0%,#E2E8F0 100%); display: flex; align-items: flex-start; justify-content: center; padding: 28px; }
    .hiw-ats-preview-frame { width: 100%; max-width: 320px; }

    /* Job Tailoring */
    .hiw-tailor-card { max-width: 1080px; margin: 0 auto; background: white; border: 1px solid #E5E7EB; border-radius: 24px; box-shadow: 0 12px 40px rgba(15,23,42,0.06); padding: 32px; }
    .hiw-tailor-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 24px; }
    .hiw-tailor-col-head { display: flex; align-items: center; gap: 7px; font-size: 12px; font-weight: 800; color: #94A3B8; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 12px; }
    .hiw-tailor-text { background: #FAFBFF; border: 1px solid #F1F5F9; border-radius: 14px; padding: 18px; }
    .hiw-tailor-text p { margin: 0; font-size: 13.5px; color: #374151; line-height: 1.8; }
    .hiw-kw { background: #F1F5F9; color: #64748B; border-radius: 5px; padding: 1px 4px; font-weight: 700; transition: all 0.3s ease; }
    .hiw-kw.active { background: #DCFCE7; color: #059669; }
    .hiw-tailor-footer { display: flex; align-items: center; justify-content: space-between; gap: 20px; flex-wrap: wrap; padding-top: 20px; border-top: 1px solid #F1F5F9; }
    .hiw-tailor-score span:first-child { display: block; font-size: 11px; font-weight: 800; color: #94A3B8; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 4px; }
    .hiw-tailor-score-row { display: flex; align-items: center; gap: 10px; }
    .hiw-tailor-score-value { font-size: 30px; font-weight: 900; letter-spacing: -1px; }
    .hiw-tailor-score-delta { display: inline-flex; align-items: center; gap: 4px; background: #ECFDF5; color: #059669; font-size: 12px; font-weight: 800; padding: 4px 9px; border-radius: 999px; }

    /* Interview practice */
    .hiw-interview-card { max-width: 720px; margin: 0 auto; background: white; border: 1px solid #E5E7EB; border-radius: 24px; box-shadow: 0 12px 40px rgba(15,23,42,0.06); padding: 36px; text-align: center; }
    .hiw-interview-question { background: #FAFBFF; border: 1px solid #F1F5F9; border-radius: 14px; padding: 18px 22px; margin-bottom: 24px; text-align: left; }
    .hiw-interview-q-label { font-size: 11px; font-weight: 800; color: #94A3B8; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 8px; }
    .hiw-interview-question p { margin: 0; font-size: 15.5px; font-weight: 700; color: #0F172A; line-height: 1.5; }
    .hiw-interview-waveform { display: flex; align-items: center; justify-content: center; gap: 3px; height: 56px; margin-bottom: 24px; }
    .hiw-interview-waveform span { width: 4px; height: 56px; border-radius: 3px; background: linear-gradient(180deg,#FCA5A5,#EF1D28); display: inline-block; }
    .hiw-interview-actions { margin-bottom: 8px; }
    .hiw-interview-feedback { text-align: left; margin-top: 24px; padding-top: 24px; border-top: 1px solid #F1F5F9; }
    .hiw-interview-feedback-row { display: flex; align-items: center; gap: 20px; margin-bottom: 20px; }
    .hiw-interview-feedback-title { font-size: 15px; font-weight: 800; color: #0F172A; margin-bottom: 6px; }
    .hiw-interview-feedback-row p { margin: 0; font-size: 13px; color: #64748B; line-height: 1.6; }
    .hiw-interview-improvements-title { display: flex; align-items: center; gap: 7px; font-size: 12.5px; font-weight: 800; color: #92400E; margin-bottom: 10px; }
    .hiw-interview-improvement { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #374151; padding: 8px 0; border-top: 1px solid #F8FAFC; }
    .hiw-interview-improvement svg { color: #059669; flex-shrink: 0; }
    .hiw-interview-cta { margin-top: 24px; padding-top: 20px; border-top: 1px solid #F1F5F9; }

    /* Kanban */
    .hiw-kanban-scroll { overflow-x: auto; scrollbar-width: none; -ms-overflow-style: none; margin: 0 -48px; padding: 4px 48px 16px; }
    .hiw-kanban-scroll::-webkit-scrollbar { display: none; }
    .hiw-kanban-board { display: flex; gap: 18px; width: max-content; min-width: 100%; }
    .hiw-kanban-col { width: 260px; background: white; border: 1px solid #E5E7EB; border-radius: 18px; padding: 16px; box-shadow: 0 4px 20px rgba(15,23,42,0.04); }
    .hiw-kanban-col-head { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 800; color: #0F172A; margin-bottom: 14px; }
    .hiw-kanban-col-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
    .hiw-kanban-col-count { margin-left: auto; background: #F1F5F9; color: #64748B; font-size: 11px; font-weight: 800; padding: 2px 8px; border-radius: 999px; }
    .hiw-kanban-cards { display: flex; flex-direction: column; gap: 10px; }
    .hiw-kanban-card { background: #FAFBFF; border: 1px solid #F1F5F9; border-left: 3px solid; border-radius: 12px; padding: 12px 14px; }
    .hiw-kanban-card-role { font-size: 13px; font-weight: 800; color: #0F172A; margin-bottom: 4px; }
    .hiw-kanban-card-company { display: flex; align-items: center; gap: 5px; font-size: 12px; color: #64748B; margin-bottom: 4px; }
    .hiw-kanban-card-date { display: flex; align-items: center; gap: 5px; font-size: 11px; color: #94A3B8; margin-bottom: 8px; }
    .hiw-kanban-card-badge { display: inline-block; font-size: 10px; font-weight: 800; padding: 3px 8px; border-radius: 999px; }

    /* Before / After */
    .hiw-ba-list { display: flex; flex-direction: column; gap: 16px; max-width: 980px; margin: 0 auto; }
    .hiw-ba-row { display: grid; grid-template-columns: 1fr 32px 1fr; align-items: center; gap: 8px; }
    .hiw-ba-col { border-radius: 16px; padding: 18px 20px; }
    .hiw-ba-before { background: #FAFAFA; border: 1.5px solid #E5E7EB; }
    .hiw-ba-after { background: #F0FDF4; border: 1.5px solid #BBF7D0; }
    .hiw-ba-label { font-size: 10.5px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 8px; }
    .hiw-ba-before .hiw-ba-label { color: #94A3B8; }
    .hiw-ba-after .hiw-ba-label { color: #059669; }
    .hiw-ba-col p { margin: 0; font-size: 13.5px; line-height: 1.6; }
    .hiw-ba-before p { color: #94A3B8; text-decoration: line-through; }
    .hiw-ba-after p { color: #111827; font-weight: 600; }
    .hiw-ba-tag { display: inline-flex; align-items: center; gap: 5px; margin-top: 10px; background: #DCFCE7; color: #059669; font-size: 11px; font-weight: 800; padding: 3px 9px; border-radius: 999px; }
    .hiw-ba-arrow { color: #CBD5E1; justify-self: center; }

    /* Why cards */
    .hiw-why-grid { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 18px; }
    .hiw-why-card { background: white; border: 1px solid #E5E7EB; border-radius: 20px; padding: 26px 22px; transition: box-shadow 0.2s; }
    .hiw-why-card:hover { box-shadow: 0 14px 34px rgba(15,23,42,0.08); }
    .hiw-why-icon { width: 44px; height: 44px; border-radius: 12px; background: #FFF1F2; color: #DC2626; display: flex; align-items: center; justify-content: center; margin-bottom: 16px; }
    .hiw-why-title { font-size: 15.5px; font-weight: 800; color: #0F172A; margin-bottom: 8px; }
    .hiw-why-card p { margin: 0; color: #64748B; font-size: 13px; line-height: 1.6; }

    /* Metrics */
    .hiw-metrics-grid { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 20px; }
    .hiw-metric-card { background: white; border: 1px solid #E5E7EB; border-radius: 20px; padding: 30px 20px; text-align: center; box-shadow: 0 4px 20px rgba(15,23,42,0.05); transition: box-shadow 0.25s ease, border-color 0.25s ease; }
    .hiw-metric-card:hover { box-shadow: 0 20px 48px rgba(15,23,42,0.1); border-color: #FBD3D5; }
    .hiw-metric-icon { width: 48px; height: 48px; border-radius: 14px; background: #FFF1F2; color: #EF1D28; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; }
    .hiw-metric-value { font-size: 32px; font-weight: 900; color: #0F172A; letter-spacing: -1px; margin-bottom: 6px; }
    .hiw-metric-label { font-size: 13px; color: #64748B; font-weight: 600; }

    /* FAQ */
    .hiw-faq-list { display: flex; flex-direction: column; gap: 12px; max-width: 780px; margin: 0 auto; }
    .hiw-faq-item { background: white; border: 1.5px solid #E5E7EB; border-radius: 16px; overflow: hidden; transition: border-color 0.25s ease, box-shadow 0.25s ease; }
    .hiw-faq-item.open { border-color: #FECACA; box-shadow: 0 10px 30px rgba(239,29,40,0.08); }
    .hiw-faq-trigger { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 20px 24px; background: transparent; border: none; cursor: pointer; text-align: left; font-family: inherit; }
    .hiw-faq-trigger span:first-child { font-size: 15.5px; font-weight: 700; color: #111827; line-height: 1.4; }
    .hiw-faq-icon { position: relative; width: 30px; height: 30px; border-radius: 9px; background: #F9FAFB; border: 1px solid #E5E7EB; flex-shrink: 0; transition: background 0.25s ease, border-color 0.25s ease; }
    .hiw-faq-item.open .hiw-faq-icon { background: #FFF1F2; border-color: #FECDD3; }
    .hiw-faq-icon .bar { position: absolute; top: 50%; left: 50%; background: #6B7280; border-radius: 2px; }
    .hiw-faq-icon .bar.h { width: 12px; height: 2px; margin: -1px 0 0 -6px; }
    .hiw-faq-icon .bar.v { width: 2px; height: 12px; margin: -6px 0 0 -1px; }
    .hiw-faq-item.open .hiw-faq-icon .bar { background: #EF1D28; }
    .hiw-faq-answer { padding: 0 24px 22px; font-size: 14.5px; color: #6B7280; line-height: 1.8; }

    /* Final CTA */
    .hiw-cta-outer { padding: 20px 48px 96px; }
    .hiw-cta-card { max-width: 900px; margin: 0 auto; background: linear-gradient(150deg, #060D1A 0%, #0D1120 50%, #0A1428 100%); border-radius: 28px; overflow: hidden; position: relative; padding: 64px 48px; border: 1px solid #1A2A3E; text-align: center; }
    .hiw-cta-mesh { position: absolute; inset: 0; background-image: radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px); background-size: 28px 28px; pointer-events: none; }
    .hiw-cta-glow { position: absolute; top: -100px; left: 50%; transform: translateX(-50%); width: 500px; height: 300px; background: radial-gradient(ellipse, rgba(239,29,40,0.18) 0%, transparent 65%); pointer-events: none; }
    .hiw-cta-card h2 { position: relative; margin: 0 0 28px; font-size: 34px; font-weight: 900; color: white; letter-spacing: -0.8px; }
    .hiw-cta-card button { position: relative; }
    .hiw-cta-sub { position: relative; margin-top: 16px; font-size: 12.5px; color: rgba(255,255,255,0.5); }

    /* Responsive */
    @media (max-width: 1100px) {
      .hiw-metrics-grid { grid-template-columns: repeat(2, minmax(0,1fr)); }
      .hiw-ai-grid { grid-template-columns: repeat(2, minmax(0,1fr)); }
      .hiw-why-grid { grid-template-columns: repeat(2, minmax(0,1fr)); }
      .hiw-ats-card { grid-template-columns: 1fr; }
      .hiw-ats-right { display: none; }
    }
    @media (max-width: 900px) {
      .hiw-hero { padding: 64px 24px 80px; }
      .hiw-hero-inner { flex-direction: column; gap: 48px; text-align: center; }
      .hiw-hero-left { flex: none; }
      .hiw-hero-left p { margin-left: auto; margin-right: auto; }
      .hiw-workflow-strip { justify-content: center; }
      .hiw-hero-ctas { justify-content: center; }
      .hiw-hero-left h1 { font-size: 38px; }
      .hiw-section { padding: 56px 24px; }
      .hiw-section-tint { padding-left: 24px; padding-right: 24px; }
      .hiw-kanban-scroll { margin: 0 -24px; padding: 4px 24px 16px; }
      .hiw-journey-track { flex-direction: column; align-items: center; width: 100%; max-width: 340px; margin: 0 auto; }
      .hiw-journey-card { width: 100%; }
      .hiw-journey-arrow { width: auto; min-height: 44px; }
      .hiw-journey-arrow-line { width: 2px; height: 28px; background: linear-gradient(180deg,#FED7D7,#EF1D28); }
      .hiw-journey-arrow-icon { transform: rotate(90deg); }
      .hiw-demo-grid { grid-template-columns: 1fr; }
      .hiw-tailor-grid { grid-template-columns: 1fr; }
      .hiw-ba-row { grid-template-columns: 1fr; }
      .hiw-ba-arrow { transform: rotate(90deg); justify-self: flex-start; margin-left: 20px; }
    }
    @media (max-width: 640px) {
      .hiw-metrics-grid { grid-template-columns: 1fr 1fr; }
      .hiw-ai-grid { grid-template-columns: 1fr; }
      .hiw-why-grid { grid-template-columns: 1fr; }
      .hiw-hero-left h1 { font-size: 30px; letter-spacing: -1px; }
      .hiw-hero-right { display: none; }
      .hiw-cta-card { padding: 44px 24px; }
      .hiw-cta-card h2 { font-size: 26px; }
      .hiw-interview-card { padding: 24px; }
    }
  `}</style>
);
